import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SEO, NOT_FOUND_SEO, OG_IMAGE, SITE_NAME, absoluteUrl, buildJsonLd, normalizePath } from '../seoConfig';

type MetaAttr = 'name' | 'property';

const setMeta = (attr: MetaAttr, key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setCanonical = (href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const setJsonLd = (data: object) => {
  let el = document.head.querySelector<HTMLScriptElement>('script#page-jsonld');
  if (!el) {
    el = document.createElement('script');
    el.id = 'page-jsonld';
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

/**
 * Keeps <title>, meta description, canonical, Open Graph / Twitter tags and JSON-LD
 * in sync with the current route. Renders nothing.
 */
const SeoManager: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const path = normalizePath(pathname);
    const known = SEO[path];
    const seo = known || NOT_FOUND_SEO;
    const url = known ? absoluteUrl(path) : absoluteUrl('/');

    document.title = seo.title;
    setMeta('name', 'description', seo.description);
    setMeta('name', 'robots', seo.noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large');
    setCanonical(url);

    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', SITE_NAME);
    setMeta('property', 'og:title', seo.title);
    setMeta('property', 'og:description', seo.description);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:image', OG_IMAGE);
    setMeta('property', 'og:image:alt', 'Zemantics: AI solutions and the Catalyst Suite');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', seo.title);
    setMeta('name', 'twitter:description', seo.description);
    setMeta('name', 'twitter:image', OG_IMAGE);

    if (known) setJsonLd(buildJsonLd(path, seo));
    else document.head.querySelector('script#page-jsonld')?.remove();
  }, [pathname]);

  return null;
};

export default SeoManager;
